'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import PricingModal from '@/components/PricingModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Campaign {
  id: string;
  plan: string;
  payment_status: string;
  billing_name: string | null;
  billing_email: string | null;
  amount: number;
  currency: string;
  created_at: string;
  ad_title: string | null;
  ad_type: string | null;
  ad_status: string | null;
  impressions: number;
  clicks: number;
  video_id: string | null;
  payment_intent_id: string | null;
}

interface AdDetails {
  id: string;
  title: string;
  impressions: number;
  clicks: number;
  ad_status: string;
  schedule_start: string | null;
  schedule_end: string | null;
  placement: string;
  link_url: string;
  thumbnail_url: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PLAN_LABELS: Record<string, string> = {
  'banner-ad-weekly': 'Banner Ad — Weekly',
  'banner-ad-monthly': 'Banner Ad — Monthly',
  'video-ad-weekly': 'Video Ad — Weekly',
  'video-ad-monthly': 'Video Ad — Monthly',
  'featured-video-weekly': 'Featured Video — Weekly',
  'vip-creator': 'VIP Creator',
  'vip-creator-pro': 'VIP Creator Pro',
};

const PLAN_CATEGORY: Record<string, string> = {
  'banner-ad-weekly': 'banner',
  'banner-ad-monthly': 'banner',
  'video-ad-weekly': 'video',
  'video-ad-monthly': 'video',
  'featured-video-weekly': 'featured',
  'vip-creator': 'vip',
  'vip-creator-pro': 'vip',
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ctr(impressions: number, clicks: number): string {
  if (!impressions) return '0.00%';
  return ((clicks / impressions) * 100).toFixed(2) + '%';
}

function statusColor(status: string): string {
  switch (status) {
    case 'active': return 'text-green-400 bg-green-400/10 border-green-400/30';
    case 'paused': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
    case 'ended': return 'text-text-muted bg-surface-tertiary border-stroke-primary';
    case 'succeeded': return 'text-green-400 bg-green-400/10 border-green-400/30';
    case 'pending': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
    default: return 'text-text-secondary bg-surface-tertiary border-stroke-primary';
  }
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  ad: AdDetails;
  onClose: () => void;
  onSave: (updated: Partial<AdDetails>) => Promise<void>;
}

function EditModal({ ad, onClose, onSave }: EditModalProps) {
  const [title, setTitle] = useState(ad.title);
  const [linkUrl, setLinkUrl] = useState(ad.link_url);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ title: title.trim(), link_url: linkUrl.trim() });
      onClose();
    } catch {
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="bg-surface-secondary border border-stroke-primary rounded-[4px] w-full max-w-md p-6 animate-fade-in">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-text-primary font-bold text-lg uppercase tracking-wide">Edit Campaign</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest mb-1.5">Ad Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2.5 rounded-[4px] focus:outline-none focus:border-brand-red transition-colors"
              placeholder="Enter ad title"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest mb-1.5">Destination URL</label>
            <input
              type="url"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2.5 rounded-[4px] focus:outline-none focus:border-brand-red transition-colors"
              placeholder="https://yoursite.com"
            />
          </div>
        </div>

        {error && (
          <p className="mt-3 text-red-400 text-xs border border-red-400/30 bg-red-400/10 rounded-[4px] px-3 py-2">{error}</p>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 border border-stroke-primary text-text-secondary hover:text-text-primary text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Campaign Row ─────────────────────────────────────────────────────────────

interface CampaignRowProps {
  campaign: Campaign;
  adDetails: AdDetails | null;
  onEdit: (ad: AdDetails) => void;
  onTogglePause: (adId: string, currentStatus: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
}

function CampaignRow({ campaign, adDetails, onEdit, onTogglePause, expanded, onToggleExpand }: CampaignRowProps) {
  const planLabel = PLAN_LABELS[campaign.plan] || campaign.plan;
  const category = PLAN_CATEGORY[campaign.plan] || 'other';
  const isAdCampaign = category === 'banner' || category === 'video';

  const impressions = adDetails?.impressions ?? campaign.impressions ?? 0;
  const clicks = adDetails?.clicks ?? campaign.clicks ?? 0;
  const adStatus = adDetails?.ad_status ?? campaign.ad_status ?? campaign.payment_status;

  return (
    <div className="border border-stroke-primary rounded-[4px] overflow-hidden">
      {/* Row header */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-surface-card cursor-pointer hover:bg-surface-tertiary transition-colors"
        onClick={onToggleExpand}
      >
        {/* Category icon */}
        <div className="w-8 h-8 rounded-[4px] bg-surface-tertiary border border-stroke-primary flex items-center justify-center flex-shrink-0">
          {category === 'banner' && (
            <svg className="w-4 h-4 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z" />
            </svg>
          )}
          {category === 'video' && (
            <svg className="w-4 h-4 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
          {category === 'featured' && (
            <svg className="w-4 h-4 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          )}
          {category === 'vip' && (
            <svg className="w-4 h-4 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-text-primary text-sm font-bold truncate">{planLabel}</p>
          <p className="text-text-muted text-xs">{formatDate(campaign.created_at)}</p>
        </div>

        {/* Status badge */}
        <span className={`hidden sm:inline-flex items-center text-xs font-bold uppercase px-2 py-0.5 rounded-[4px] border ${statusColor(adStatus)}`}>
          {adStatus}
        </span>

        {/* Amount */}
        <span className="text-brand-red font-bold text-sm ml-2">{formatCents(campaign.amount)}</span>

        {/* Expand chevron */}
        <svg
          className={`w-4 h-4 text-text-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-stroke-primary bg-surface-secondary px-4 py-4 animate-fade-in">
          {/* Stats row */}
          {isAdCampaign && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2.5 text-center">
                <p className="text-text-muted text-xs uppercase tracking-widest mb-0.5">Impressions</p>
                <p className="text-text-primary font-bold text-lg">{impressions.toLocaleString()}</p>
              </div>
              <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2.5 text-center">
                <p className="text-text-muted text-xs uppercase tracking-widest mb-0.5">Clicks</p>
                <p className="text-text-primary font-bold text-lg">{clicks.toLocaleString()}</p>
              </div>
              <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2.5 text-center">
                <p className="text-text-muted text-xs uppercase tracking-widest mb-0.5">CTR</p>
                <p className="text-brand-red font-bold text-lg">{ctr(impressions, clicks)}</p>
              </div>
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4 text-sm">
            <div>
              <span className="text-text-muted text-xs uppercase tracking-widest">Plan</span>
              <p className="text-text-primary">{planLabel}</p>
            </div>
            <div>
              <span className="text-text-muted text-xs uppercase tracking-widest">Amount Paid</span>
              <p className="text-text-primary">{formatCents(campaign.amount)}</p>
            </div>
            <div>
              <span className="text-text-muted text-xs uppercase tracking-widest">Purchased</span>
              <p className="text-text-primary">{formatDate(campaign.created_at)}</p>
            </div>
            <div>
              <span className="text-text-muted text-xs uppercase tracking-widest">Payment</span>
              <p className={`font-bold ${campaign.payment_status === 'succeeded' ? 'text-green-400' : 'text-yellow-400'}`}>
                {campaign.payment_status}
              </p>
            </div>
            {adDetails?.placement && (
              <div>
                <span className="text-text-muted text-xs uppercase tracking-widest">Placement</span>
                <p className="text-text-primary capitalize">{adDetails.placement}</p>
              </div>
            )}
            {adDetails?.link_url && (
              <div>
                <span className="text-text-muted text-xs uppercase tracking-widest">Destination</span>
                <a
                  href={adDetails.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-red hover:underline truncate block max-w-[180px]"
                >
                  {adDetails.link_url}
                </a>
              </div>
            )}
          </div>

          {/* Actions */}
          {isAdCampaign && adDetails && (
            <div className="flex gap-2 pt-2 border-t border-stroke-primary">
              <button
                onClick={() => onEdit(adDetails)}
                className="flex items-center gap-1.5 border border-stroke-primary text-text-secondary hover:text-text-primary hover:border-stroke-light text-xs font-bold uppercase px-3 py-2 rounded-[4px] transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
              <button
                onClick={() => onTogglePause(adDetails.id, adDetails.ad_status)}
                className={`flex items-center gap-1.5 text-xs font-bold uppercase px-3 py-2 rounded-[4px] transition-colors border ${
                  adDetails.ad_status === 'paused' ?'border-green-400/40 text-green-400 hover:bg-green-400/10' :'border-yellow-400/40 text-yellow-400 hover:bg-yellow-400/10'
                }`}
              >
                {adDetails.ad_status === 'paused' ? (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Resume
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Pause
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdvertiserDashboard() {
  const { user } = useAuth();
  const supabase = createClient();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [adDetailsMap, setAdDetailsMap] = useState<Record<string, AdDetails>>({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingAd, setEditingAd] = useState<AdDetails | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [email, setEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchCampaigns = useCallback(async (billingEmail: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('billing_email', billingEmail)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const rows: Campaign[] = (data || []).map((r: any) => ({
        id: r.id,
        plan: r.plan || '',
        payment_status: r.payment_status || 'pending',
        billing_name: r.billing_name || null,
        billing_email: r.billing_email || null,
        amount: r.amount || 0,
        currency: r.currency || 'usd',
        created_at: r.created_at || '',
        ad_title: r.ad_title || null,
        ad_type: r.ad_type || null,
        ad_status: r.ad_status || null,
        impressions: r.impressions || 0,
        clicks: r.clicks || 0,
        video_id: r.video_id || null,
        payment_intent_id: r.payment_intent_id || null,
      }));
      setCampaigns(rows);

      // Fetch linked video_ads for ad campaigns
      const adCampaigns = rows.filter(c => {
        const cat = PLAN_CATEGORY[c.plan];
        return cat === 'banner' || cat === 'video';
      });

      if (adCampaigns.length > 0) {
        // Try to match by billing_email in video_ads advertiser field or by payment_intent_id
        const { data: ads } = await supabase
          .from('video_ads')
          .select('*')
          .order('created_at', { ascending: false });

        if (ads && ads.length > 0) {
          const map: Record<string, AdDetails> = {};
          // Simple heuristic: match by advertiser email or most recent ads
          ads.forEach((ad: any) => {
            const detail: AdDetails = {
              id: ad.id,
              title: ad.title || '',
              impressions: ad.impressions || 0,
              clicks: ad.clicks || 0,
              ad_status: ad.ad_status || 'draft',
              schedule_start: ad.schedule_start || null,
              schedule_end: ad.schedule_end || null,
              placement: ad.placement || '',
              link_url: ad.link_url || '',
              thumbnail_url: ad.thumbnail_url || '',
            };
            // Match by advertiser field containing email
            if (ad.advertiser && ad.advertiser.toLowerCase().includes(billingEmail.toLowerCase())) {
              // Associate with the first unmatched campaign
              const unmatched = adCampaigns.find(c => !map[c.id]);
              if (unmatched) map[unmatched.id] = detail;
            }
          });
          setAdDetailsMap(map);
        }
      }
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      fetchCampaigns(user.email);
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleLookup = async () => {
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setLookupError('Please enter a valid email address.');
      return;
    }
    setLookupError('');
    setLookupLoading(true);
    setEmail(trimmed);
    await fetchCampaigns(trimmed);
    setLookupLoading(false);
  };

  const handleTogglePause = async (adId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'paused' ? 'active' : 'paused';
    try {
      const { error } = await supabase
        .from('video_ads')
        .update({ ad_status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', adId);
      if (error) throw error;

      setAdDetailsMap(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(prev).map(([cid, ad]) =>
            ad.id === adId ? [cid, { ...ad, ad_status: newStatus }] : [cid, ad]
          )
        ),
      }));
      showToast(`Campaign ${newStatus === 'paused' ? 'paused' : 'resumed'} successfully.`);
    } catch {
      showToast('Failed to update campaign status.');
    }
  };

  const handleSaveEdit = async (updated: Partial<AdDetails>) => {
    if (!editingAd) return;
    const { error } = await supabase
      .from('video_ads')
      .update({ ...updated, updated_at: new Date().toISOString() })
      .eq('id', editingAd.id);
    if (error) throw error;

    setAdDetailsMap(prev => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(prev).map(([cid, ad]) =>
          ad.id === editingAd.id ? [cid, { ...ad, ...updated }] : [cid, ad]
        )
      ),
    }));
    showToast('Campaign updated successfully.');
  };

  // ── Summary stats ──────────────────────────────────────────────────────────
  const totalSpend = campaigns.reduce((sum, c) => sum + (c.payment_status === 'succeeded' ? c.amount : 0), 0);
  const totalImpressions = Object.values(adDetailsMap).reduce((sum, a) => sum + a.impressions, 0);
  const totalClicks = Object.values(adDetailsMap).reduce((sum, a) => sum + a.clicks, 0);
  const activeCampaigns = campaigns.filter(c => c.payment_status === 'succeeded').length;

  return (
    <div className="min-h-screen bg-surface-primary text-text-primary">
      {/* Header */}
      <header className="border-b border-stroke-primary bg-surface-secondary sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link href="/" className="flex items-center gap-2 text-brand-red hover:opacity-80 transition-opacity">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-xs font-bold uppercase tracking-widest hidden sm:inline">Back</span>
            </Link>
            <div className="w-px h-5 bg-stroke-primary" />
            <h1 className="text-text-primary font-bold text-base uppercase tracking-wide">Advertiser Dashboard</h1>
          </div>
          <button
            onClick={() => setShowPricing(true)}
            className="flex items-center gap-1.5 bg-brand-red hover:bg-brand-red-hover text-white text-xs font-bold uppercase px-3 py-2 rounded-[4px] transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Campaign
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Email lookup (for non-logged-in users) */}
        {!user && (
          <div className="mb-8 bg-surface-secondary border border-stroke-primary rounded-[4px] p-5">
            <p className="text-text-secondary text-sm mb-3">
              Enter the email address used when purchasing your ad campaign to view your dashboard.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLookup()}
                placeholder="billing@yourcompany.com"
                className="flex-1 bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2.5 rounded-[4px] focus:outline-none focus:border-brand-red transition-colors"
              />
              <button
                onClick={handleLookup}
                disabled={lookupLoading}
                className="bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors disabled:opacity-50"
              >
                {lookupLoading ? 'Looking up…' : 'Look Up'}
              </button>
            </div>
            {lookupError && (
              <p className="mt-2 text-red-400 text-xs">{lookupError}</p>
            )}
          </div>
        )}

        {/* Logged-in greeting */}
        {user && (
          <div className="mb-6">
            <p className="text-text-muted text-sm">
              Showing campaigns for <span className="text-brand-red font-bold">{user.email}</span>
            </p>
          </div>
        )}

        {/* Summary stats */}
        {(email || user) && !loading && campaigns.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {[
              { label: 'Total Spend', value: formatCents(totalSpend), accent: true },
              { label: 'Active Campaigns', value: activeCampaigns.toString(), accent: false },
              { label: 'Total Impressions', value: totalImpressions.toLocaleString(), accent: false },
              { label: 'Total Clicks', value: totalClicks.toLocaleString(), accent: false },
            ].map(stat => (
              <div key={stat.label} className="bg-surface-card border border-stroke-primary rounded-[4px] px-4 py-3">
                <p className="text-text-muted text-xs uppercase tracking-widest mb-1">{stat.label}</p>
                <p className={`font-bold text-xl ${stat.accent ? 'text-brand-red' : 'text-text-primary'}`}>{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-surface-card border border-stroke-primary rounded-[4px] animate-pulse" />
            ))}
          </div>
        )}

        {/* No email entered yet */}
        {!loading && !email && !user && (
          <div className="text-center py-16">
            <svg className="w-12 h-12 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-text-muted text-sm">Enter your billing email above to view your campaigns.</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && email && campaigns.length === 0 && (
          <div className="text-center py-16 border border-stroke-primary rounded-[4px] bg-surface-card">
            <svg className="w-12 h-12 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a13.937 13.937 0 01-3.417.894l-2.147-6.15M15 10a3 3 0 11-6 0 3 3 0 016 0zM12 2a10 10 0 100 20 10 10 0 000-20z" />
            </svg>
            <p className="text-text-primary font-bold mb-1">No campaigns found</p>
            <p className="text-text-muted text-sm mb-5">No ad campaigns found for <span className="text-brand-red">{email}</span>.</p>
            <button
              onClick={() => setShowPricing(true)}
              className="bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase px-5 py-2.5 rounded-[4px] transition-colors"
            >
              Purchase a Campaign
            </button>
          </div>
        )}

        {/* Campaign list */}
        {!loading && campaigns.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-text-secondary text-xs uppercase tracking-widest">
                {campaigns.length} Campaign{campaigns.length !== 1 ? 's' : ''}
              </h2>
            </div>
            {campaigns.map(campaign => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
                adDetails={adDetailsMap[campaign.id] || null}
                onEdit={setEditingAd}
                onTogglePause={handleTogglePause}
                expanded={expandedId === campaign.id}
                onToggleExpand={() => setExpandedId(expandedId === campaign.id ? null : campaign.id)}
              />
            ))}
          </div>
        )}

        {/* Help section */}
        <div className="mt-12 border-t border-stroke-primary pt-8">
          <h3 className="text-text-secondary text-xs uppercase tracking-widest mb-4">Need Help?</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-surface-card border border-stroke-primary rounded-[4px] p-4">
              <p className="text-text-primary text-sm font-bold mb-1">Purchase a New Campaign</p>
              <p className="text-text-muted text-xs mb-3">Browse banner ads, video ads, and featured placements.</p>
              <button
                onClick={() => setShowPricing(true)}
                className="text-brand-red text-xs font-bold uppercase hover:underline"
              >
                View Pricing →
              </button>
            </div>
            <div className="bg-surface-card border border-stroke-primary rounded-[4px] p-4">
              <p className="text-text-primary text-sm font-bold mb-1">Campaign Not Showing?</p>
              <p className="text-text-muted text-xs mb-3">Make sure you're using the same email address from your purchase receipt.</p>
              <Link href="/" className="text-brand-red text-xs font-bold uppercase hover:underline">
                Return Home →
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Edit modal */}
      {editingAd && (
        <EditModal
          ad={editingAd}
          onClose={() => setEditingAd(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Pricing modal */}
      {showPricing && <PricingModal isOpen={showPricing} onClose={() => setShowPricing(false)} />}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-surface-overlay border border-stroke-light text-text-primary text-sm font-bold px-5 py-3 rounded-[4px] shadow-xl z-50 animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}
